/**
 * Auth Context - Provides authentication abstraction for both dev and production modes
 *
 * In DEV_MODE (REACT_APP_DEV_MODE=true): Uses mock authentication
 * In production: Uses Auth0
 */

import { createContext, useContext } from 'react';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';

// Dev mode configuration
export const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

// Mock user for dev mode — uses real Auth0 ID so bookmarks are shared across environments
export const mockUser = {
  sub: 'google-oauth2|113041720185893359128',
  name: 'Blake',
  email: 'dev@aiquire.local',
  picture: null,
  email_verified: true
};

// Default mock auth values
const defaultMockAuth = {
  isAuthenticated: true,
  isLoading: false,
  user: mockUser,
  loginWithRedirect: () => console.log('Dev mode: login skipped'),
  logout: () => {
    console.log('Dev mode: logout - redirecting to landing');
    window.location.href = '/';
  },
  getAccessTokenSilently: () => Promise.resolve('dev-token'),
  getIdTokenClaims: () => Promise.resolve({ __raw: 'dev-token' }),
  error: null
};

// Auth context for dev mode
export const AuthContext = createContext(defaultMockAuth);

/**
 * Custom useAuth hook that works in both dev and production modes
 * In dev mode: reads from AuthContext (which App.js provides with mock values)
 * In production: uses real Auth0 hook
 */
export const useAuth = () => {
  // Always call useContext - this is the dev mode context
  const devAuth = useContext(AuthContext);

  // In dev mode, return the context value (mock auth)
  if (DEV_MODE) {
    return devAuth;
  }

  // In production, use real Auth0
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAuth0Real();
};

// Re-export for backwards compatibility
export const useAuth0 = useAuth;

export default { useAuth, useAuth0, AuthContext, mockUser, DEV_MODE };
